import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Snackbar, Alert, Container, Typography } from '@mui/material';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import EditIcon from '@mui/icons-material/Edit';
import { participantHeader, tosText } from '../assets/terms';

const CallbackPage = () => {
    const { linkId } = useParams();
    const [phone, setPhone] = useState('');
    const [updatedPhone, setUpdatedPhone] = useState('');
    const [isLinkExpired, setIsLinkExpired] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`/api/callback/${linkId}`);
                const data = response.data;
                setPhone(data.phone_number);

                // Check if the link is expired
                if (data.status !== 'new') {
                    setIsLinkExpired(true);
                }
            } catch (error) {
                console.error('Error fetching callback data:', error);
            }
        };

        fetchData();
    }, [linkId]);

    const handleCallMeNow = async () => {
        if (isLinkExpired) {
            return;
        }

        try {
            const payload = { updated_phone_number: phone };
            const response = await axios.post(`/api/callback/${linkId}/initiate-call`, payload);

            if (response.status === 200) {
                setSnackbarMessage('Call initiated successfully');
                setSnackbarSeverity('success');
            } else {
                setSnackbarMessage('Failed to initiate call');
                setSnackbarSeverity('error');
            }
        } catch (error) {
            console.error('Error initiating call:', error);
            setSnackbarMessage('Failed to initiate call');
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true);
        }
    };

    const handleOpenModal = () => {
        setUpdatedPhone(phone);
        setIsModalOpen(true);
    };

    const handleCloseModal = (savePhoneNumber = false) => {
        if (savePhoneNumber) {
            setPhone(updatedPhone);
        }
        setIsModalOpen(false);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <Container maxWidth="sm" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            {isLinkExpired ? (
                <Alert severity="error" sx={{ width: '100%' }}>
                    This link is expired and cannot be used.
                </Alert>
            ) : (
                <div>
                    <h1>You are invited to participate in a Marketing class phone interview conducted by an AI agent</h1>
                    <Typography variant="body1" component="p" style={{ marginBottom: '16px' }}>
                        {participantHeader}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="div"
                        style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            border: '1px solid #ccc',
                            padding: '8px',
                            fontFamily: 'monospace',
                            marginBottom: '16px',
                        }}
                    >
                        {tosText}
                    </Typography>
                    <Typography variant="body1" component="p" style={{ marginBottom: '16px' }}>
                        We will call you at <span style={{ textDecoration: 'underline dotted' }}>{phone}</span>
                        <IconButton onClick={handleOpenModal} size="small">
                            <EditIcon />
                        </IconButton>
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleCallMeNow}>
                        Call me now
                    </Button>

                    <Dialog open={isModalOpen} onClose={handleCloseModal}>
                        <DialogTitle>Update Phone Number</DialogTitle>
                        <DialogContent>
                            <PhoneInput
                                defaultCountry="US"
                                value={updatedPhone}
                                onChange={(value) => setUpdatedPhone(value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => handleCloseModal(false)} color="primary">
                                Cancel
                            </Button>
                            <Button onClick={() => handleCloseModal(true)} color="primary" variant="contained">
                                Save
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        onClose={handleSnackbarClose}
                    >
                        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </div>
            )}
        </Container>
    );
};

export default CallbackPage; 